import React, { useMemo, useState } from "react";
import { Drawer } from "vaul";
import { Minus, Plus, X } from "lucide-react";
import { SmartImage } from "./media";
import { brl, discountPct } from "../../lib/format";

export default function ProductSheet({ product, products, open, onClose, onAdd }) {
  if (!product) return null;
  return (
    <Drawer.Root open={open} onOpenChange={(o) => !o && onClose()} dismissible={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[70] bg-black/70" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-[71] mx-auto max-w-md h-[92vh] rounded-t-3xl menu-bg menu-text border-t menu-border outline-none flex flex-col" data-testid="product-sheet">
          <Inner product={product} products={products} onClose={onClose} onAdd={onAdd} />
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function Inner({ product, products, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [sel, setSel] = useState({}); // groupId -> [itemId]
  const promo = product.promo_price && product.promo_price < product.price;
  const base = promo ? product.promo_price : product.price;

  const toggle = (group, item) => {
    setSel((prev) => {
      const cur = prev[group.id] || [];
      const single = group.max === 1;
      if (single) return { ...prev, [group.id]: [item.id] };
      if (cur.includes(item.id)) return { ...prev, [group.id]: cur.filter((x) => x !== item.id) };
      if (group.max && cur.length >= group.max) return prev;
      return { ...prev, [group.id]: [...cur, item.id] };
    });
  };

  const chosenAddons = useMemo(() => {
    const out = [];
    (product.addon_groups || []).forEach((g) => {
      (sel[g.id] || []).forEach((iid) => {
        const it = g.items.find((x) => x.id === iid);
        if (it) out.push({ groupId: g.id, groupName: g.name, itemId: it.id, name: it.name, price: it.price });
      });
    });
    return out;
  }, [sel, product]);

  const addonsSum = chosenAddons.reduce((s, a) => s + (a.price || 0), 0);
  const unit = base + addonsSum;
  const total = unit * qty;

  const missing = (product.addon_groups || []).filter(
    (g) => g.required && (sel[g.id] || []).length < (g.min || 1)
  );

  const upsellProducts = useMemo(() => {
    if (!["produto", "both"].includes(product.upsell_location)) return [];
    const byId = Object.fromEntries((products || []).map((p) => [p.id, p]));
    return (product.upsell_product_ids || []).map((id) => byId[id]).filter((p) => p && !p.sold_out).slice(0, 6);
  }, [product, products]);

  const add = () => {
    if (missing.length) return;
    onAdd({ product, qty, addons: chosenAddons, note, unit });
    onClose();
  };

  return (
    <>
      <button onClick={onClose} data-testid="sheet-close" className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white"><X size={18} /></button>
      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="h-56 mb-2"><SmartImage src={product.image} className="h-full w-full" /></div>
        <div className="p-5 relative">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-head text-2xl font-extrabold leading-tight menu-text">{product.name}</h2>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-head text-xl font-extrabold price-final">{brl(base)}</span>
            {promo && <><span className="text-sm menu-faint line-through">{brl(product.price)}</span>
              <span className="text-[11px] font-bold bg-rose-600 text-white px-2 py-0.5 rounded-full">-{discountPct(product.price, product.promo_price)}%</span></>}
          </div>
          <p className="text-sm menu-muted leading-relaxed mt-3">{product.description}</p>

          {(product.addon_groups || []).map((g) => (
            <div key={g.id} className="mt-5">
              <div className="flex items-center justify-between">
                <h4 className="font-head font-bold menu-text">{g.name}</h4>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${g.required ? "bg-[#FF6B00]/20 text-[#FF6B00]" : "menu-chip menu-muted"}`}>
                  {g.required ? "Obrigatório" : "Opcional"}{g.max ? ` · até ${g.max}` : ""}
                </span>
              </div>
              <div className="mt-2 space-y-2">
                {g.items.map((it) => {
                  const checked = (sel[g.id] || []).includes(it.id);
                  return (
                    <button key={it.id} onClick={() => toggle(g, it)} data-testid={`addon-${it.id}`}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border text-left ${checked ? "border-[#FF6B00] bg-[#FF6B00]/10" : "menu-border menu-surface"}`}>
                      <span className="text-sm menu-text">{it.name}</span>
                      <span className="text-sm menu-muted">{it.price ? `+ ${brl(it.price)}` : "Grátis"}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-5">
            <h4 className="font-head font-bold mb-2 menu-text">Observação</h4>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="sheet-note"
              placeholder="Ex: sem cebola, ponto da carne..." rows={2}
              className="w-full rounded-xl menu-surface border menu-border p-3 text-sm outline-none focus:border-[#FF6B00] menu-text placeholder:text-[color:var(--m-faint)]" />
          </div>

          {upsellProducts.length > 0 && (
            <div className="mt-6">
              <h4 className="font-head font-bold menu-text">Complete seu pedido</h4>
              <div className="flex gap-3 overflow-x-auto no-scrollbar mt-2 pb-1">
                {upsellProducts.map((u) => (
                  <button key={u.id} onClick={() => onAdd({ product: u, qty: 1, addons: [], note: "", unit: u.promo_price || u.price })}
                    data-testid={`upsell-product-${u.id}`}
                    className="shrink-0 w-32 rounded-xl menu-surface border menu-border overflow-hidden text-left">
                    <SmartImage src={u.image} className="h-20 w-full" />
                    <div className="p-2">
                      <p className="text-xs font-semibold line-clamp-1 menu-text">{u.name}</p>
                      <p className="text-xs price-final font-bold mt-0.5">+ {brl(u.promo_price || u.price)}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 border-t menu-border menu-bg flex items-center gap-3">
        <div className="flex items-center gap-3 rounded-full menu-chip px-2 py-1.5">
          <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus" className="h-8 w-8 rounded-full flex items-center justify-center menu-text"><Minus size={16} /></button>
          <span className="w-5 text-center font-bold menu-text" data-testid="qty-value">{qty}</span>
          <button onClick={() => setQty((q) => q + 1)} data-testid="qty-plus" className="h-8 w-8 rounded-full flex items-center justify-center menu-text"><Plus size={16} /></button>
        </div>
        <button onClick={add} disabled={missing.length > 0} data-testid="sheet-add"
          className={`flex-1 rounded-full py-3.5 font-bold text-white transition-transform active:scale-[0.98] ${missing.length ? "bg-zinc-700" : "hmmm-gradient hmmm-glow"}`}>
          {missing.length ? `Selecione: ${missing[0].name}` : `Adicionar · ${brl(total)}`}
        </button>
      </div>
    </>
  );
}
